(() => {
  if (window.__tlShareMenuInitialized) return;
  window.__tlShareMenuInitialized = true;

  let openMenu = null;
  let openTrigger = null;

  const getTrigger = (menu) => document.querySelector(`[aria-controls="${CSS.escape(menu.id)}"]`);
  const isInline = (menu) => menu?.hasAttribute('data-tl-share-inline');

  const sanitizedShareUrl = (menu) => {
    const target = new URL(menu.dataset.shareUrl || window.location.href, window.location.origin);
    const current = new URL(window.location.href);
    target.search = '';
    target.hash = '';

    if (menu.dataset.shareType === 'product' && target.pathname === current.pathname) {
      const variant = current.searchParams.get('variant');
      if (variant && /^\d+$/.test(variant)) target.searchParams.set('variant', variant);
    }

    return target.toString();
  };

  const updateDestinations = (menu) => {
    const url = sanitizedShareUrl(menu);
    const title = menu.dataset.shareTitle || document.title;
    const image = menu.dataset.shareImage || '';
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);

    menu.querySelector('[data-tl-share-destination="line"]')?.setAttribute(
      'href',
      `https://social-plugins.line.me/lineit/share?url=${encodedUrl}&text=${encodedTitle}`
    );
    menu.querySelector('[data-tl-share-destination="x"]')?.setAttribute(
      'href',
      `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`
    );
    menu.querySelector('[data-tl-share-destination="facebook"]')?.setAttribute(
      'href',
      `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
    );
    const pinterest = menu.querySelector('[data-tl-share-destination="pinterest"]');
    if (pinterest) {
      const media = image ? `&media=${encodeURIComponent(new URL(image, window.location.href).toString())}` : '';
      pinterest.setAttribute(
        'href',
        `https://www.pinterest.com/pin/create/button/?url=${encodedUrl}${media}&description=${encodedTitle}`
      );
    }

    const fallbackInput = menu.querySelector('[data-tl-share-fallback-input]');
    if (fallbackInput) fallbackInput.value = url;
    return { url, title };
  };

  const setStatus = (menu, message) => {
    const status = document.querySelector(`[data-tl-share-status-for="${CSS.escape(menu.id)}"]`);
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      status.textContent = message;
    });
  };

  const close = (menu = openMenu, restoreFocus = true) => {
    if (!menu || menu.hidden || isInline(menu)) return;
    const trigger = getTrigger(menu) || openTrigger;
    menu.hidden = true;
    menu.querySelector('[data-tl-share-fallback]')?.setAttribute('hidden', '');
    trigger?.setAttribute('aria-expanded', 'false');
    openMenu = null;
    openTrigger = null;
    if (restoreFocus) trigger?.focus({ preventScroll: true });
  };

  const open = (menu, trigger) => {
    if (openMenu && openMenu !== menu) close(openMenu, false);
    updateDestinations(menu);
    const nativeButton = menu.querySelector('[data-tl-share-native]');
    if (nativeButton) nativeButton.hidden = typeof navigator.share !== 'function';
    menu.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    openMenu = menu;
    openTrigger = trigger;
    menu.querySelector('a, button:not([hidden])')?.focus({ preventScroll: true });
  };

  const copyWithFallback = async (menu) => {
    const { url } = updateDestinations(menu);
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(url);
      setStatus(menu, 'リンクをコピーしました');
      if (!isInline(menu)) close(menu);
      return;
    } catch (clipboardError) {
      const fallback = menu.querySelector('[data-tl-share-fallback]');
      const input = menu.querySelector('[data-tl-share-fallback-input]');
      if (!fallback || !input) return;
      fallback.hidden = false;
      input.value = url;
      input.focus();
      input.select();
      try {
        if (document.execCommand('copy')) {
          setStatus(menu, 'リンクをコピーしました');
          if (!isInline(menu)) close(menu);
          return;
        }
      } catch (fallbackError) {
        console.warn('[tl-share-menu] Copy fallback failed.', fallbackError);
      }
      setStatus(menu, 'URLを選択してコピーしてください');
    }
  };

  document.addEventListener('click', async (event) => {
    const trigger = event.target.closest('[data-tl-share-trigger]');
    if (trigger) {
      const menu = document.getElementById(trigger.getAttribute('aria-controls'));
      if (!menu) return;
      event.preventDefault();
      if (menu.hidden) open(menu, trigger);
      else close(menu);
      return;
    }

    const copyButton = event.target.closest('[data-tl-share-copy]');
    if (copyButton) {
      event.preventDefault();
      await copyWithFallback(copyButton.closest('[data-tl-share-menu]'));
      return;
    }

    const nativeButton = event.target.closest('[data-tl-share-native]');
    if (nativeButton) {
      event.preventDefault();
      const menu = nativeButton.closest('[data-tl-share-menu]');
      const payload = updateDestinations(menu);
      try {
        await navigator.share(payload);
        if (!isInline(menu)) close(menu);
      } catch (error) {
        if (error?.name !== 'AbortError') {
          console.warn('[tl-share-menu] Native sharing failed.', error);
          setStatus(menu, '共有を開始できませんでした');
        }
      }
      return;
    }

    const destination = event.target.closest('[data-tl-share-destination]');
    if (destination) {
      const menu = destination.closest('[data-tl-share-menu]');
      if (!isInline(menu)) close(menu);
      return;
    }

    if (openMenu && !openMenu.contains(event.target) && event.target !== openTrigger) close(openMenu);
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openMenu) {
      event.preventDefault();
      close(openMenu);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (openMenu && event.target.contains(openMenu)) {
      openMenu = null;
      openTrigger = null;
    }
  });

  const initializeInlineMenus = (root = document) => {
    root.querySelectorAll('[data-tl-share-inline]').forEach((menu) => {
      updateDestinations(menu);
      const nativeButton = menu.querySelector('[data-tl-share-native]');
      if (nativeButton) nativeButton.hidden = typeof navigator.share !== 'function';
    });
  };

  document.addEventListener('shopify:section:load', (event) => initializeInlineMenus(event.target));
  initializeInlineMenus();
})();
